<template>
  <modal ref="modal" height="400px" large title="Message"
         :no-scroll="mode === 'message' && !showAiCard" :onClose="closing">
    <template #buttons>
      <b-button v-if="stack && !!stack.length" variant="link" class="text-white d-none d-sm-inline" size="sm"
                @click="goback">BACK
      </b-button>
      <b-button v-else-if="$listeners.done" variant="link" class="text-white d-none d-sm-inline" size="sm"
                @click="$emit('done')">BACK
      </b-button>
    </template>
    <template #top>
      <template v-if="!!message">
        <div class="d-flex mb-0 flex-column">
          <template v-if="footer_mode !== 'reply'">
            <div class="mb-auto">
              <div class="mb-auto mr-auto h5 d-flex">
                <text-copy class="mr-auto">{{ message.gli }}</text-copy>
                <div class="ml-auto">
                  <b-badge v-if="message.ai_triage === 'u'" variant="danger" class="mr-1">Urgent</b-badge>
                  <b-badge v-else-if="message.ai_triage === 'o'" variant="success" class="mr-1">Opportunity</b-badge>
                  <b-badge :variant="message.source === 'y' || message.source === 'z' ? 'warning' : 'primary'">{{ message.source_description }}</b-badge>
                </div>
              </div>
            </div>

            <div class="d-flex">
              <div class="iheader">Date:</div>
              {{ message.created | date }}
            </div>
            <template v-if="message.is_sent_message">
              <div class="d-flex">
                <div class="iheader">From:</div>
                {{ message.send_by_user && message.send_by_user.name }}
              </div>
              <div class="d-flex">
                <div class="iheader">To:</div>
                {{ message.address_display }}
              </div>
            </template>
            <div v-else class="d-flex">
              <div class="iheader">From:</div>
              {{ message.address_display }}
              <!-- who the sender is in OMS; the details are on the Contact tab -->
              <span v-for="c in contacts" :key="c.kind" class="ml-2 small">
                <b-link @click="mode = 'contact'">{{ c.label }}: {{ c.data.name || c.data.company || c.data.gli }}</b-link>
              </span>
            </div>
            <!-- who else was on the thread, so the CC list is visible without
                 opening a reply -->
            <div v-if="ccDisplay" class="d-flex">
              <div class="iheader">CC:</div>
              <div class="text-truncate" v-b-tooltip.hover :title="ccDisplay">{{ ccDisplay }}</div>
            </div>
            <div class="text-muted">{{ message.subject }}</div>
          </template>

          <div class="d-flex mb-2 flex-column" v-else-if="!!draft">
            <div class="d-flex">
              <div class="iheader">To:</div>
              {{ draft.address_display }}
            </div>
            <div class="text-muted">{{ draft.subject }}</div>
          </div>


          <mode-selector :class="{'mt-3': footer_mode != 'reply'}" v-model="mode" :modes="modes" breakpoint="lg"/>
        </div>
      </template>
    </template>

    <div v-if="!!message" class="h-100">
      <!-- campaign provenance: this message is either a campaign send or a
           reply that threaded back to one -->
      <b-alert v-if="message.campaign" show variant="info" class="py-2 px-3 mb-2 small">
        <font-awesome-icon :icon="['fas', 'bullhorn']" class="mr-2"/>
        {{ message.campaign.is_reply ? 'Reply to campaign' : 'Sent as part of campaign' }}
        <b-link :href="message.campaign.route" target="_blank" class="font-weight-bold ml-1">
          {{ message.campaign.gli }} — {{ message.campaign.name }}
        </b-link>
      </b-alert>

      <div v-if="mode === 'message'" class="h-100 d-flex flex-column">
        <message-ai-card v-if="showAiCard && !aiNotesHidden" :message="message" class="mb-2 flex-shrink-0"/>
        <div v-else-if="showAiCard" class="small text-right mb-1 flex-shrink-0">
          <b-link class="text-muted" @click="AiNotes.set(false)">
            <font-awesome-icon :icon="['fas', 'robot']" class="mr-1"/>Show AI notes
          </b-link>
        </div>
        <message-content :message="message" class="flex-fill"/>
      </div>
      <message-history :url="working_url" v-if="mode === 'history'" @click="histClick"/>
      <div v-show="mode === 'notes'">
        <notes height="auto" class="mt-" @input="(n) => message.notes = n" :url="working_url + '/note'"
               :in-modal="mode === 'notes'"
               read="messages.notes.read" write="messages.notes.write" />
      </div>
      <div v-if="mode === 'attachments'">
        <div v-for="file in message.files.filter(f => !f.is_image)" :key="file.id" class="mb-1">
          <font-awesome-icon :icon="['fas', 'paperclip']" class="mr-1 text-muted"/>
          <b-link :href="file.url" target="_blank">{{ file.orig_filename }}</b-link>
        </div>
        <photo-gallery readonly :photos="message.files.filter(f => f.is_image)" v-if="message.files.some(f => f.is_image)"
                       class="mt-2"/>
      </div>
      <message-info :message="message" v-show="mode == 'info'"/>
      <b-row v-if="mode === 'contact'">
        <b-col v-for="c in contacts" :key="c.kind" cols="12" :lg="12 / contacts.length" class="mb-3">
          <contact-match-panel :data="c.data"/>
        </b-col>
      </b-row>
      <template v-if="mode === 'drafts'">
        <b-list-group>
          <b-list-group-item :variant="(draft && draft.id) === d.id ? 'primary': ''" :action="!draft"
                             v-for="d in message.drafts" :key="d.id" @click="!draft && reply(d)">
            <div class="d-flex w-100 justify-content-between">
              <span class="mb-1">By: {{ d.by_user.name }}</span>
              <small>{{ d.created | timediff }}</small>
            </div>
            <small>{{ d.content_plain }}</small>
          </b-list-group-item>
        </b-list-group>
      </template>
    </div>

    <!-- for some reason this breaks the modal close event capturing,  send the modal-close event to force a refresh of the datatable -->
    <message-compose ref="compose" @done="direct_done" v-else-if="!message && !!draft" :msg="draft"/>

    <template #footer>
      <template v-if="!!message">
        <template v-if="footer_mode == 'assign'">
          <b-row class="w-100">
            <b-col cols="12" lg="5" class="pt-2">
              <b-form-group label="Assign To" label-for="assigned_to">
                <b-select id="assigned_to" v-model="assigned_to"
                          :options="users.map(u => {return {text: u.name, value: u.id}})"/>
              </b-form-group>
            </b-col>
            <b-col cols="12" lg="5" class="pt-2">
              <b-textarea rows="3" v-model="note" placeholder="Parent Notes"/>
            </b-col>
            <b-col cols="12" lg="2" class="pt-2">
              <b-row>
                <b-col cols="6" lg="12" class="mt-2 mt-lg-0">
                  <b-btn block variant="outline-primary" @click="execute('assign')" size="sm">Assign
                  </b-btn>
                </b-col>
                <b-col cols="6" lg="12" class="mt-2 mt-lg-2">
                  <b-btn block variant="outline-secondary" @click="footer_mode = null" size="sm">Cancel
                  </b-btn>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </template>

        <message-compose @done="done" ref="compose" v-else-if="footer_mode == 'reply' && !!message"
                         :msg="draft || message" :initial-cc="reply_cc"/>

        <template v-else>
          <!-- left of the footer; the task buttons and Reply stay on the right -->
          <b-btn variant="outline-primary" size="sm" class="float-left" @click="sendToAssistant()"
                 v-if="message && message.replyable && can('chat.use')"
                 title="Take this email and have the assistant draft the reply">
            <font-awesome-icon :icon="['fas', 'robot']"/>
            Send to assistant
          </b-btn>
          <b-button-group>
            <b-btn class="text-capitalize" @click="execute(task)"
                   :variant="{delete: 'outline-secondary', assign: 'outline-primary', unassign: 'outline-secondary', done: 'outline-primary'}[task]"
                   size="sm" v-for="task in message.available_tasks" :key="task" v-text="task"
                   :disabled="task == 'assign' && !users"/>
          </b-button-group>

          <b-btn variant="primary" size="sm" class="ml-4" @click="reply()"
                 v-if="message && message.replyable">
            <font-awesome-icon icon="fa-solid fa-reply"/>
            Reply
          </b-btn>
          <b-btn variant="outline-primary" size="sm" @click="undelete" v-if="message.state=='x'">Undelete</b-btn>
        </template>
      </template>
    </template>

  </modal>
</template>

<script>
import {sourceCanCc} from '@/lib/message-source'
import MessageContent from "./message-content.vue";
import MessageHistory from "./message-history.vue";
import MessageInfo from "./message-info.vue";
import MessageCompose from "./message-compose.vue";
import ContactMatchPanel from "./contact-match-panel.vue";
import MessageAiCard from "./message-ai-card.vue";
import AiNotes from "@/lib/ai-notes-pref";
import ChatModal from "@/components/chat/chat-modal.vue";
import MessageAssistantAsk from "./message-assistant-ask.vue";

export default {
  name: "message",
  components: {MessageAiCard, ContactMatchPanel, MessageCompose, MessageInfo, MessageHistory, MessageContent},
  props: {
    url: String,
  },
  data() {
    return {
      message: null,
      AiNotes,
      reply_cc: [],
      mode: 'message',
      footer_mode: null,
      assigned_to: null,
      note: null,
      draft: null,
      working_url: this.url,
      stack: [],
      matches: {lead: null, customer: null, vendor: null}
    }
  },
  computed: {
    // CC list for the modal header
    ccDisplay() {
      return ((this.message && this.message.cc_addresses) || [])
          .map(a => a.name ? `${a.name} <${a.email}>` : a.email)
          .join(', ')
    },

    users() {
      return this.$store.state.settings.defaults.users
    },
    self() {
      return this.users && this.users.find(f => f.id === this.$store.state.auth.id)
    },

    aiNotesHidden() {
      return AiNotes.hidden
    },

    showAiCard() {
      return !!(this.message && this.message.ai_has_tab)
    },

    contacts() {
      return ['lead', 'customer', 'vendor']
          .filter(k => this.matches[k])
          .map(k => ({kind: k, label: k.charAt(0).toUpperCase() + k.slice(1), data: this.matches[k]}))
    },

    // only tabs with something in them: an empty Files or Photos tab is noise
    modes() {
      const m = this.message
      const modes = [{text: 'Message', value: 'message'}]

      if (m && m.history)
        modes.push({text: 'History (' + m.history + ')', value: 'history'})

      if (this.can('messages.notes.read'))
        modes.push({text: 'Notes (' + ((m && m.notes && m.notes.all.length) || 0) + ')', value: 'notes'})

      if (m && m.files.length)
        modes.push({text: 'Attachments (' + m.files.length + ')', value: 'attachments'})

      if (m && !m.is_sent_message && m.drafts.length)
        modes.push({text: 'Drafts (' + m.drafts.length + ')', value: 'drafts'})

      if (this.contacts.length)
        modes.push({text: this.contacts.length === 1 ? this.contacts[0].label : 'Contact', value: 'contact'})

      modes.push({text: 'Info', value: 'info'})

      return modes
    }
  },

  async created() {
    this.assigned_to = this.users.find(f => f.id === this.$store.state.auth.id).id
    await this.refresh()

    if (this.message && this.message.notes && this.message.notes.all.length) {
      await this.confirm('Message has notes, review?')
      this.mode = 'notes'
    }
  },

  methods: {
    goback() {
      this.mode = 'message'
      this.working_url = this.stack.pop()
      this.refresh()
    },
    histClick(msg) {
      if (this.working_url == msg.url)
        return

      this.mode = 'message'
      this.stack.push(this.working_url)
      this.working_url = msg.url
      this.refresh()
    },
    done(r) {
      this.draft = null
      this.footer_mode = null

      r ? this.$refs.modal.close() : this.refresh()
    },
    direct_done(r) {
      if (r) {
        this.draft = null
        this.footer_mode = null
        this.$root.$emit('modal-close')
        this.$refs.modal.close()
      }
    },
    async closing() {
      this.$store.dispatch('badges_load')
      return this.footer_mode == 'reply' ? await this.$refs.compose.closing() : true
    },
    async undelete() {
      await this.asyncAction('/api/message', 'POST', {data: {_restore: this.message.id}})
      this.toast('Message restored')
      await this.refresh()
    },
    async refresh() {
      const message = await this.asyncAction(this.working_url, 'GET')
      if (message.state == 'f') {
        this.footer_mode = 'reply'
        this.draft = message
        this.message = this.draft.reply_parent
        this.message && (this.working_url = this.message._links.self_url.href)
      } else {
        this.message = message
      }
      this.loadMatches()
    },

    async loadMatches() {
      this.matches = {lead: null, customer: null, vendor: null}
      const email = this.message && this.message.account_address
      if (!email || typeof email !== 'string' || !email.includes('@'))
        return
      try {
        const res = await this.asyncAction('/api/message/contact-match', 'GET', {noload: true, params: {email}})
        this.matches = {lead: res.lead, customer: res.customer, vendor: res.vendor}
      } catch {
        // best-effort only
      }
    },
    async reply(draft) {
      if (draft) {
        this.draft = await this.asyncAction(draft.url, 'GET')
        if (this.draft.state != 'f') {
          this.draft = null
          this.alert('Message no longer a draft')
          return
        }
      }

      if (!await this.takeMessage())
        return

      await this.offerReplyAll()
      this.footer_mode = 'reply'

    },

    // Take the email and open a new assistant chat that reads it (and the AI notes)
    // and drafts the reply; the assistant sends only when told to in the chat.
    async sendToAssistant() {
      let force = false
      if (this.message.state == 'a' && this.message.assigned_to != this.self.id) {
        if (!await this.confirm('Message assigned to ' + (this.message.assigned_to_user ? this.message.assigned_to_user.name : 'UNKNOWN') + '.   Take over and send to the assistant?'))
          return
        force = true
      }

      const instruction = await this.$dialog.showAndWait(MessageAssistantAsk, {subject: this.message.subject || ''})
      if (instruction === null || instruction === undefined)
        return

      const r = await this.asyncAction(`/api/message/${this.message.id}/assistant`, 'POST', {data: {_force: force, instruction}})
      this.message = {...this.message, state: r.state, assigned_to: r.assigned_to, assigned_to_user: r.assigned_to_user}
      this.$dialog.show(ChatModal, {
        context: {type: 'message', id: this.message.id, gli: this.message.gli, title: this.message.subject},
        conversation: r.conversation,
      })
    },

    // Assign the message to me (asking first if someone else has it) so I can reply.
    async takeMessage() {
      let force = false
      if (this.message.state == 'a' && this.message.assigned_to != this.self.id) {
        if (!await this.confirm('Message assigned to ' + (this.message.assigned_to_user ? this.message.assigned_to_user.name : 'UNKNOWN') + '.   Take over and reply?'))
          return false

        force = true
      }

      try {
        const result = await this.asyncAction(this.message._links.assign_url.href, this.message._links.assign_url.method, {
          data: {_force: force}
        })

        this.message = {...this.message, ...result}
      } catch (e) {
        console.log(e)
        this.alert('Unable to assign message.  Unable to reply.')
        return false
      }

      return true
    },

    /**
     * Offer Reply All when the original had other recipients. Asked at the
     * moment of replying rather than left as a button in the composer, because
     * that is when the decision is actually being made - and getting it wrong
     * silently drops people off the thread.
     */
    async offerReplyAll() {
      this.reply_cc = []

      const parent = this.draft ? this.draft.reply_parent : this.message

      // an eBay or internal reply has nowhere to put a CC, so asking would
      // promise something the send path drops without a word
      if (!parent || !sourceCanCc(parent.source))
        return

      const cc = (parent && parent.reply_all_cc) || []
      if (!cc.length)
        return

      const names = cc.map(a => a.name ? `${a.name} <${a.email}>` : a.email)
      const preview = names.slice(0, 5).join('\n')
      const more = names.length > 5 ? `\n…and ${names.length - 5} more` : ''

      if (await this.confirm(
          `This message had ${names.length} other recipient${names.length === 1 ? '' : 's'}.\n\n`
          + `${preview}${more}\n\nReply to everyone? They will be CC'd.`))
        this.reply_cc = cc.map(a => a.email)
    },

    async execute(task) {
      let data = {_note: this.note}

      if (task === 'assign' && this.footer_mode != 'assign') {
        this.footer_mode = 'assign'
        return
      } else {
        data = {...data, _force: true, assigned_to: this.assigned_to}
      }
      await this.asyncAction(this.message._links[task + '_url'].href, this.message._links[task + '_url'].method, {data})

      this.toast('Message ' + {
        done: 'done',
        assign: 'assigned',
        delete: 'deleted',
        unassign: 'unassigned'
      }[task]);
      this.$refs.modal.close()
    }
  }
}
</script>

<style scoped>
.iheader {
  width: 50px
}
</style>